import { Text, View } from '@react-pdf/renderer'
import { AutoComplete, DatePicker, Form, Input, Select, Space } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import dayjs from 'dayjs'
import GeckoLogo from 'features/reporting/pdfGeneration/components/shared/GeckoLogo'
import { GradientStripe } from 'features/reporting/pdfGeneration/components/shared/GradientStripe'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { SignatureAnchor } from 'features/reporting/pdfGeneration/components/shared/SignatureAnchor'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import {
  CoverSectionData,
  DEFAULT_INSPECTION_TEMPERATURE_UNIT,
  inspectionTemperatureUnits,
  MainInfo,
  RugReportSectionId,
} from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import {
  ReportedAssetType,
  ReportMaterialType,
  XAxisStartLocationOptions,
  YAxisStartLocationOptions,
} from 'pages/reporting/reports/rug/types'
import {
  normalizeInspectionTemperature,
  serializeInspectionTemperatureForForm,
} from 'pages/reporting/reports/rug/utils/inspectionTemperature'
import { SourceIndicator } from 'pages/reporting/shared/components/SourceIndicator'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useMemo } from 'react'
import { SignoffRole, SignoffState } from 'types'
import {
  autocompleteFilterOptionWithoutExactMatch,
  autocompletePreviewDisabledProps,
} from 'utils/fde/antD'

const REPORT_DATE_FORMAT = 'MM/DD/YYYY'
export const RUG_COVER_HEADER_TEXT = 'Ultrasonics Inspection Report'

export const RUG_MANUAL_SIGNOFF_ROWS = [
  'Field Technician',
  'Data Analyst',
  'Reviewed By',
] as const

export const getRugManualContributorRows = (signoff?: SignoffState) => {
  const savedSignoffs = {
    [SignoffRole.SUPERVISOR]: signoff?.supervisorSignoff,
    [SignoffRole.DATA_ANALYST]: signoff?.dataAnalystSignoff,
    [SignoffRole.REVIEWER]: signoff?.reviewerSignoff,
  }

  return [
    {
      role: SignoffRole.SUPERVISOR,
      contributorType: RUG_MANUAL_SIGNOFF_ROWS[0],
      contributor: {
        name: signoff?.supervisorSignoff?.name || '',
        certification: signoff?.supervisorSignoff?.qualifications || '',
      },
    },
    {
      role: SignoffRole.DATA_ANALYST,
      contributorType: RUG_MANUAL_SIGNOFF_ROWS[1],
      contributor: {
        name: signoff?.dataAnalystSignoff?.name || '',
        certification: signoff?.dataAnalystSignoff?.qualifications || '',
      },
    },
    {
      role: SignoffRole.REVIEWER,
      contributorType: RUG_MANUAL_SIGNOFF_ROWS[2],
      contributor: {
        name: signoff?.reviewerSignoff?.name || '',
        certification: signoff?.reviewerSignoff?.qualifications || '',
      },
    },
  ].filter(({ role }) => savedSignoffs[role])
}

const INSPECTION_TEMPERATURE_UNIT_OPTIONS = inspectionTemperatureUnits.map(
  (unit) => ({
    label: unit.name,
    value: unit.id,
  })
)

const requiredCoverPageFields = [
  'mainInfo.customerName',
  'mainInfo.customerLocation',
  'mainInfo.assetName',
  'mainInfo.assetType',
  'mainInfo.jobNumber',
  'coverSectionData.inspectionStartDate',
  'coverSectionData.inspectionEndDate',
]

const ContributorRow: React.FC<{
  role: SignoffRole
  contributorType: string
  contributor?: {
    name?: string
    certification?: string
  }
}> = ({ role, contributorType, contributor }) => {
  return (
    <View style={PDFStyles.coverContributorGroup}>
      <View style={PDFStyles.contributorRow}>
        <Text style={PDFStyles.contributorHeaderText}>{contributorType}</Text>
        <Text style={PDFStyles.contributorHeaderText}>Certification</Text>
        <Text style={PDFStyles.contributorHeaderText}>Date</Text>
        <Text style={PDFStyles.contributorHeaderText}>Signature</Text>
      </View>
      <View style={[PDFStyles.contributorBox, PDFStyles.coverContributorBox]}>
        <Text style={PDFStyles.contributorValueText}>
          {contributor?.name || ''}
        </Text>
        <Text style={PDFStyles.contributorValueText}>
          {contributor?.certification || ''}
        </Text>
        <SignatureAnchor kind='date' role={role} />
        <SignatureAnchor kind='signature' role={role} />
      </View>
    </View>
  )
}

export const RugCoverPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useRugReportOps()

  const signoff = useMemo(() => formData.signoff, [formData.signoff])
  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])
  const coverData = useMemo(
    () => formData.coverSectionData,
    [formData.coverSectionData]
  )

  const oneDayInspection = useMemo(() => {
    const startDate = dayjs(coverData.inspectionStartDate)
    const endDate = dayjs(coverData.inspectionEndDate)
    return startDate.isSame(endDate, 'day')
  }, [coverData.inspectionStartDate, coverData.inspectionEndDate])

  const methodTitle = 'Rapid Ultrasonic Gridding (RUG) Examination'

  const getDatesOfInspectionTitle = () => {
    if (!coverData.inspectionStartDate || !coverData.inspectionEndDate) {
      return 'Not specified'
    }

    const startDate = dayjs(coverData.inspectionStartDate).format(
      REPORT_DATE_FORMAT
    )
    const endDate = dayjs(coverData.inspectionEndDate).format(
      REPORT_DATE_FORMAT
    )

    if (oneDayInspection) {
      return startDate
    }

    return `${startDate} - ${endDate}`
  }

  return (
    <ReportPage
      id='cover'
      size='LETTER'
      wrap={false}
      fillPage
      pageStyle={PDFStyles.defaultLetterPageStyle}
      tracker={{
        sectionId: RugReportSectionId.COVER,
        onRender,
        registerOnce: false,
      }}
      contentStyle={[PDFStyles.content, PDFStyles.coverContent]}
      contentBottomPadding={0}
      footer={
        <PageFooter revisionNumber={formData.revisionMetadata.revisionNumber} />
      }>
      <View style={PDFStyles.coverHeader}>
        <GeckoLogo />
        <View style={PDFStyles.coverHeaderContent}>
          <Text style={PDFStyles.coverHeaderText}>{RUG_COVER_HEADER_TEXT}</Text>
          <GradientStripe />
        </View>
      </View>

      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageTitleText}>Customer</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>
            {mainInfo.customerName}
          </Text>
          <Text style={PDFStyles.coverPageValueSmall}>
            {mainInfo.customerLocation}
          </Text>
        </View>
      </View>

      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageShortDash} />
          <Text style={PDFStyles.coverPageTitleText}>Method</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>{methodTitle}</Text>
        </View>
      </View>

      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageTitleText}>Asset</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>{mainInfo.assetName}</Text>
        </View>
      </View>

      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageShortDash} />
          <Text style={PDFStyles.coverPageTitleText}>Gecko Job Number</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>{mainInfo.jobNumber}</Text>
        </View>
      </View>

      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageTitleText}>
            {oneDayInspection ? 'Date of Inspection' : 'Dates of Inspection'}
          </Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>
            {getDatesOfInspectionTitle()}
          </Text>
        </View>
      </View>

      <View style={{ flexGrow: 1 }} />
      <View style={PDFStyles.coverSignatureSection}>
        {getRugManualContributorRows(signoff).map(
          ({ role, contributorType, contributor }) => (
            <ContributorRow
              key={role}
              role={role}
              contributorType={contributorType}
              contributor={contributor}
            />
          )
        )}
      </View>
    </ReportPage>
  )
}

const RenderPDF = RugCoverPDF

interface CoverPageFormValues
  extends
    Omit<MainInfo, 'componentAxisStartLocations' | 'inspectionTemperature'>,
    Omit<CoverSectionData, 'inspectionStartDate' | 'inspectionEndDate'> {
  inspectionTemperature?: {
    value?: string
    unit?: string
  }
  inspectionStartDate: dayjs.Dayjs
  inspectionEndDate: dayjs.Dayjs
  [key: `xAxis_${string}` | `yAxis_${string}`]: string | undefined
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<CoverPageFormValues>()
  const {
    formData,
    handleCoverSectionFinish,
    handleMainInfoFinish,
    fieldSources,
    selectedAssetModel,
    selectedComponents,
  } = useRugReportOps()

  const sourceFor = React.useCallback(
    (path: string) => fieldSources?.[path],
    [fieldSources]
  )

  const initialValues = React.useMemo(() => {
    const componentAxisValues: Record<string, string> = {}
    if (formData.mainInfo.componentAxisStartLocations) {
      Object.entries(formData.mainInfo.componentAxisStartLocations).forEach(
        ([componentId, locations]) => {
          componentAxisValues[`xAxis_${componentId}`] =
            locations.xAxisStartLocation
          componentAxisValues[`yAxis_${componentId}`] =
            locations.yAxisStartLocation
        }
      )
    }

    return {
      ...formData.mainInfo,
      inspectionTemperature: serializeInspectionTemperatureForForm(
        formData.mainInfo.inspectionTemperature
      ) ?? {
        unit: DEFAULT_INSPECTION_TEMPERATURE_UNIT.id,
      },
      ...componentAxisValues,
      inspectionStartDate: formData.coverSectionData.inspectionStartDate
        ? dayjs(formData.coverSectionData.inspectionStartDate)
        : undefined,
      inspectionEndDate: formData.coverSectionData.inspectionEndDate
        ? dayjs(formData.coverSectionData.inspectionEndDate)
        : undefined,
    }
  }, [formData.mainInfo, formData.coverSectionData])

  React.useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form, initialValues])

  const onFinish = async (values: CoverPageFormValues) => {
    const componentAxisStartLocations: Record<
      string,
      { xAxisStartLocation: string; yAxisStartLocation: string }
    > = {}

    selectedComponents?.forEach((component) => {
      const xKey = `xAxis_${component.id}` as const
      const yKey = `yAxis_${component.id}` as const
      if (values[xKey] !== undefined || values[yKey] !== undefined) {
        componentAxisStartLocations[component.id] = {
          xAxisStartLocation: values[xKey] || '',
          yAxisStartLocation: values[yKey] || '',
        }
      }
    })

    const mainInfoUpdate: MainInfo = {
      assetName: values.assetName,
      jobNumber: values.jobNumber,
      assetType: values.assetType,
      assetMaterial: values.assetMaterial,
      customerName: values.customerName,
      customerLocation: values.customerLocation,
      inspectionTemperature: normalizeInspectionTemperature(
        values.inspectionTemperature?.value,
        values.inspectionTemperature?.unit
      ),
      componentAxisStartLocations,
    }

    const coverData: CoverSectionData = {
      inspectionStartDate:
        values.inspectionStartDate.format(REPORT_DATE_FORMAT),
      inspectionEndDate: values.inspectionEndDate.format(REPORT_DATE_FORMAT),
    }

    await handleCoverSectionFinish(coverData)
    await handleMainInfoFinish(mainInfoUpdate, { showSuccessMessage: false })
  }

  return (
    <StyledForm
      form={form}
      name='rugCoverPageForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item
        name='customerName'
        label={
          <Space>
            <span>Customer Name</span>
            <SourceIndicator
              form={form}
              name='customerName'
              source={sourceFor('mainInfo.customerName')}
              initialValue={form.getFieldValue('customerName')}
            />
          </Space>
        }>
        <Input />
      </Form.Item>

      <Form.Item
        name='customerLocation'
        label={
          <Space>
            <span>Customer Location</span>
            <SourceIndicator
              form={form}
              name='customerLocation'
              source={sourceFor('mainInfo.customerLocation')}
              initialValue={form.getFieldValue('customerLocation')}
            />
          </Space>
        }>
        <Input />
      </Form.Item>

      <Form.Item
        name='assetName'
        label={
          <Space>
            <span>Asset Name</span>
            <SourceIndicator
              form={form}
              name='assetName'
              source={sourceFor('mainInfo.assetName')}
              initialValue={form.getFieldValue('assetName')}
            />
          </Space>
        }>
        <Input />
      </Form.Item>

      <Form.Item
        name='assetType'
        label={
          <Space>
            <span>Asset Type</span>
            <SourceIndicator
              form={form}
              name='assetType'
              source={sourceFor('mainInfo.assetType')}
              initialValue={form.getFieldValue('assetType')}
            />
          </Space>
        }>
        <AutoComplete
          {...autocompletePreviewDisabledProps}
          options={Object.values(ReportedAssetType).map((status) => {
            return { value: status }
          })}
          placeholder='Select Asset Type'
          filterOption={autocompleteFilterOptionWithoutExactMatch}
        />
      </Form.Item>

      <Form.Item
        name='jobNumber'
        label={
          <Space>
            <span>Gecko Job Number</span>
            <SourceIndicator
              form={form}
              name='jobNumber'
              source={sourceFor('mainInfo.jobNumber')}
              initialValue={form.getFieldValue('jobNumber')}
            />
          </Space>
        }>
        <Input />
      </Form.Item>

      <Form.Item
        name='assetMaterial'
        label={
          <Space>
            <span>Asset Material</span>
            <SourceIndicator
              form={form}
              name='assetMaterial'
              source={sourceFor('mainInfo.assetMaterial')}
              initialValue={form.getFieldValue('assetMaterial')}
            />
          </Space>
        }>
        <AutoComplete
          {...autocompletePreviewDisabledProps}
          options={Object.values(ReportMaterialType).map((status) => {
            return { value: status }
          })}
          placeholder='Select Asset Material'
          filterOption={autocompleteFilterOptionWithoutExactMatch}
        />
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Inspection Temperature</span>
            <SourceIndicator
              form={form}
              name={['inspectionTemperature', 'value']}
              source={sourceFor('mainInfo.inspectionTemperature')}
              initialValue={form.getFieldValue([
                'inspectionTemperature',
                'value',
              ])}
            />
          </Space>
        }>
        <Space.Compact style={{ width: '100%' }}>
          <Form.Item name={['inspectionTemperature', 'value']} noStyle>
            <Input placeholder='e.g., 83 or Ambient' />
          </Form.Item>
          <Form.Item name={['inspectionTemperature', 'unit']} noStyle>
            <Select
              style={{ width: '96px' }}
              options={INSPECTION_TEMPERATURE_UNIT_OPTIONS}
            />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      {selectedComponents && selectedComponents.length > 0 && (
        <>
          {selectedComponents.map((component) => {
            const assetType = selectedAssetModel?.data.type

            return (
              <React.Fragment key={component.id}>
                <Form.Item
                  name={`xAxis_${component.id}`}
                  label={
                    <Space>
                      <span>X-Axis Start Location ({component.data.name})</span>
                      <SourceIndicator
                        form={form}
                        name={`xAxis_${component.id}`}
                        source={sourceFor(
                          `mainInfo.componentAxisStartLocations.${component.id}.xAxisStartLocation`
                        )}
                        initialValue={form.getFieldValue(
                          `xAxis_${component.id}`
                        )}
                      />
                    </Space>
                  }>
                  <AutoComplete
                    {...autocompletePreviewDisabledProps}
                    options={(assetType
                      ? (XAxisStartLocationOptions[assetType] ?? [])
                      : []
                    ).map((option) => ({ value: option }))}
                    placeholder='Select or Type Location'
                    filterOption={autocompleteFilterOptionWithoutExactMatch}
                  />
                </Form.Item>
                <Form.Item
                  name={`yAxis_${component.id}`}
                  label={
                    <Space>
                      <span>Y-Axis Start Location ({component.data.name})</span>
                      <SourceIndicator
                        form={form}
                        name={`yAxis_${component.id}`}
                        source={sourceFor(
                          `mainInfo.componentAxisStartLocations.${component.id}.yAxisStartLocation`
                        )}
                        initialValue={form.getFieldValue(
                          `yAxis_${component.id}`
                        )}
                      />
                    </Space>
                  }>
                  <AutoComplete
                    {...autocompletePreviewDisabledProps}
                    options={(assetType
                      ? (YAxisStartLocationOptions[assetType] ?? [])
                      : []
                    ).map((option) => ({ value: option }))}
                    placeholder='Select or Type Location'
                    filterOption={autocompleteFilterOptionWithoutExactMatch}
                  />
                </Form.Item>
              </React.Fragment>
            )
          })}
        </>
      )}

      <Form.Item
        name='inspectionStartDate'
        label={
          <Space>
            <span>Start Date</span>
            <SourceIndicator
              form={form}
              name='inspectionStartDate'
              source={sourceFor('coverSectionData.inspectionStartDate')}
              initialValue={form.getFieldValue('inspectionStartDate')}
            />
          </Space>
        }>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        name='inspectionEndDate'
        label={
          <Space>
            <span>End Date</span>
            <SourceIndicator
              form={form}
              name='inspectionEndDate'
              source={sourceFor('coverSectionData.inspectionEndDate')}
              initialValue={form.getFieldValue('inspectionEndDate')}
            />
          </Space>
        }>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item>
        <CantileverButton
          primary
          onClick={() => form.submit()}
          style={{ width: '100%' }}>
          Save Cover Page
        </CantileverButton>
      </Form.Item>
    </StyledForm>
  )
}

export const Cover: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: RugReportSectionId.COVER,
  description: 'Basic report information and metadata',
  title: 'Cover Page',
  requiredFields: requiredCoverPageFields,
}
